import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {Label} from './label'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'
import {SearchLabels} from '@github-ui/server-persisted-queries'
import type {SearchLabelsQuery} from '@github-ui/server-persisted-queries/types/SearchLabels'

export type GetSearchLabelsRequest = {
  owner: string
  repo: string
  query?: string
  count?: number
  labelNames?: string[]
  suggested?: boolean
}

export type GetSearchLabelsResponse = GraphQLResponse<SearchLabelsQuery>

export async function apiGetSearchLabels({
  owner,
  repo,
  query,
  count,
  labelNames,
  suggested,
}: GetSearchLabelsRequest): Promise<GetSearchLabelsResponse> {
  const params: Record<string, unknown> = {owner, repo, queryId: SearchLabels}

  if (query) {
    params['query'] = query
  }

  if (count) {
    params['count'] = count
  }

  if (labelNames && labelNames.length > 0) {
    params['labelNames'] = labelNames.join(',')
  }

  // Ask the server to embed the popularity-ranked "Suggested labels" group in
  // the response it already returns when the picker opens (before any search
  // term is typed), so suggestions arrive without an extra request. The server
  // flag-gates the field.
  if (suggested) {
    params['suggested'] = true
  }

  const requestUrl = buildItemPickerSearchUrl('labels', params)
  const request = await reactFetchJSON(requestUrl)
  return (await request.json()) as GetSearchLabelsResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildItemPickerLabelSearchQueryKey(searchParams: GetSearchLabelsRequest) {
  return [itemPickerQueryKeyBase, 'labels', searchParams]
}

export type LabelPickerQueryData = {
  repositoryId: string
  labels: Label[]
  totalCount: number
  labelsByNames?: Label[]
  suggestedLabels?: Label[]
}

/**
 * Helper function to strip quotes from label names and optionally filter labels by those names
 * @param labelNames Array of label names that may have quotes
 * @param labels Optional array of labels to filter
 * @returns Object with stripped names and optionally filtered labels
 */
function processLabelNames(labelNames: string[], labels: Label[]) {
  const strippedNames = labelNames.map(name => name.replace(/^"/, '').replace(/"$/, ''))

  return labels.filter(label => strippedNames.includes(label.name))
}

export function transformSearchLabelResponseToQueryData(
  response: SearchLabelsQuery,
  labelNames?: string[],
): LabelPickerQueryData {
  const repository = response.repository
  const labels = repository?.labels

  if (!repository || !labels) {
    throw new Error('Invalid SearchLabels response: missing repository labels data')
  }

  const labelNodes = labels.nodes?.filter(label => label !== null) ?? []
  let matchedLabels: Label[] | undefined = repository.labelsByNames?.nodes?.filter(label => label !== null)

  // If labelNames were provided, filter to only those that match after stripping quotes
  if (labelNames && labelNames.length > 0 && matchedLabels) {
    matchedLabels = processLabelNames(labelNames, matchedLabels)
  }

  // `suggestions` is only present when the picker first opens (no search term
  // typed) and the server flag is enabled, so it's absent from the generated
  // SearchLabels type — read it defensively. Each entry carries its own
  // serialized label; we just project them in the server's order and drop any
  // entry without a label.
  const suggestions = (repository as RepositoryWithSuggestions).suggestions
  const suggestedLabels = suggestions
    ?.filter((entry): entry is SuggestionEntry => entry?.label != null)
    .map(entry => entry.label)

  return {
    labels: labelNodes,
    repositoryId: repository.id,
    totalCount: labels.totalCount,
    labelsByNames: matchedLabels,
    suggestedLabels,
  }
}

// A self-contained suggestion entry from the server: the fully serialized label
// to suggest, a stable machine-readable reason ("popular", "user_frequent", ...)
// whose display copy the client owns, and a 0-based rank within that reason's
// group. It is not part of the generated SearchLabels GraphQL type.
type SuggestionEntry = {label: Label; reason: string; rank: number}
type RepositoryWithSuggestions = NonNullable<SearchLabelsQuery['repository']> & {
  suggestions?: Array<SuggestionEntry | null> | null
}
